/**
 * The field schemas every form is built from — LV §6, §117, §126, §134.
 *
 * **What belongs here, and what emphatically does not.** These schemas describe the
 * *shape of the request body*: is the field present, is it text or a decimal or a whole
 * number, how many characters can the column hold, what does an empty input mean on the
 * wire. That is what LV §126 calls validation "for usability" — the half that exists so a
 * user is told at the field rather than by a round trip.
 *
 * What stays on the server is every rule that has an opinion: whether a price is
 * plausible, whether a lead time is sane, whether a material number is already taken,
 * whether this user may set this value at this site. Re-implementing one of those here
 * would be business logic in the frontend, which LV §134 and §223 forbid outright — and
 * it would be the *second* copy of a rule, free to drift from the first.
 *
 * The line is not always obvious, so the test for it is: **could the server change this
 * rule without the client being wrong?** A price limit could. "Money has at most two
 * decimal places" could not — that is `NUMERIC(14,2)` and the wire format itself
 * (LV §117).
 *
 * **Decimals never become numbers.** `betrag` and `menge` return a *string*, normalised from the
 * German comma to the API's point. It never passes through `Number` or `parseFloat`,
 * because the rounding would already be done by the time anyone could object, and
 * `1234567.89` is where that shows (LV §117; `tests/unit/inventory-forms.test.tsx` has
 * asserted the type rather than the value since the first inventory form).
 */

import { z } from "zod";

import { istOrtszeit } from "@/lib/plant-time";

/**
 * Text the request body cannot omit. Trimmed, because " " is not an entry.
 *
 * `minLaenge` exists for the few fields where the *server* sets a floor —
 * `app/api/v1/inventory.py:722` declares the supersede reason as `Form(min_length=3)`.
 * It mirrors a server constraint and is never a minimum invented here: a client stricter
 * than the server refuses input the server would have taken, which is the mistake
 * `dezimal()` already made once (decision 126, docs/10 item 50).
 */
export function pflichttext(maxLaenge = 255, minLaenge = 1) {
  return z.string().trim().min(minLaenge).max(maxLaenge);
}

/**
 * Optional text. An empty input becomes `null`, not `""`.
 *
 * The distinction is not cosmetic: `""` overwrites a stored value with an empty string,
 * `null` clears it. Every service in this product takes `null` for "no value", so the
 * transform belongs in one place rather than in twenty-three `.trim() || null` call sites.
 */
export function text(maxLaenge = 1000) {
  return z
    .string()
    .trim()
    .max(maxLaenge)
    .transform((wert) => (wert === "" ? null : wert));
}

/**
 * The one regular expression every decimal column is checked against, by its scale.
 *
 * Each named schema below is this, applied to one column type. The names are the point:
 * the first version of this module had a single generic `dezimal()` at two decimal places,
 * and it was applied to four stock levels within the hour — a client refusing `1,125 l`
 * that the database would have stored. A name that says which column it belongs to cannot
 * be misapplied silently (decision 126, docs/10 item 50).
 *
 * `,5`, `.5` and `1,` are numbers too: the API's `Decimal` takes `.5` and `1.`, and the
 * first version refused them with a message about decimal places that did not apply.
 *
 * **Trailing zeros past the scale stay refused, on purpose.** Pydantic would take
 * `50,000` as fifty — but a German user writes `10.000` for ten thousand and `1.250` for
 * one thousand two hundred and fifty, and this pattern reads `.` and `,` alike. Allowing
 * the zeros made `10.000 €` arrive as ten euros. A refusal ("höchstens zwei
 * Nachkommastellen") is the only safe answer to a number that has two readings (#316).
 * Exponents (`1e3`) are left out too: nobody types them, and a refusal costs nothing.
 */
function nachkommastellen(stellen: number) {
  return new RegExp(`^-?(\\d+([.,]\\d{0,${stellen}})?|[.,]\\d{1,${stellen}})$`);
}

/** An optional decimal of the given scale: empty becomes `null`, the comma a point. */
function dezimalOderLeer(stellen: number, meldung: string) {
  const muster = nachkommastellen(stellen);
  return z
    .string()
    .trim()
    .refine((wert) => wert === "" || muster.test(wert), { message: meldung })
    .transform((wert) => (wert === "" ? null : wert.replace(",", ".")));
}

/** A decimal of the given scale that the request body cannot omit. */
function pflichtdezimal(stellen: number, meldung: string) {
  const muster = nachkommastellen(stellen);
  return z
    .string()
    .trim()
    .min(1)
    .refine((wert) => muster.test(wert), { message: meldung })
    .transform((wert) => wert.replace(",", "."));
}

/**
 * Money on the wire — `NUMERIC(14,2)` (LV §117).
 *
 * Accepts the German comma and hands back the API's point form, as a string. Empty
 * becomes `null`. The sign is deliberately allowed: whether a negative amount makes sense
 * is the server's rule, and a correction booking is a real case.
 *
 * **Not for quantities.** They are `NUMERIC(18,4)` and have their own schema below.
 */
export function betrag() {
  return dezimalOderLeer(2, "Betrag mit höchstens zwei Nachkommastellen.");
}

/**
 * A quantity on the wire — `NUMERIC(18,4)`, from `app/db/base.py:101` (LV §117).
 *
 * Stock levels, booked amounts, recommended counts. Four decimal places, because a
 * material measured in litres or kilograms is not counted in whole pieces.
 */
export function menge() {
  return dezimalOderLeer(4, "Menge mit höchstens vier Nachkommastellen.");
}

/**
 * A reading or a counter value — `NUMERIC(18,4)` like a quantity
 * (`app/models/inspections.py:183`, `app/models/maintenance.py:67`), and named apart
 * because it is not one: a refusal under a temperature or a counter interval should not
 * talk about a "Menge".
 */
export function messwert() {
  return dezimalOderLeer(4, "Messwert mit höchstens vier Nachkommastellen.");
}

/** A quantity the request body cannot omit. */
export function pflichtmenge() {
  return pflichtdezimal(4, "Menge mit höchstens vier Nachkommastellen.");
}

/**
 * A plant's position — `NUMERIC(9,6)`, `app/models/organisation.py:113-114` (LV §117).
 *
 * Six decimal places, which is about ten centimetres. `betrag()` would cut the position
 * to a kilometre and `menge()` would refuse it. Whether the value is a plausible latitude
 * is the server's to say.
 */
export function koordinate() {
  return dezimalOderLeer(6, "Koordinate mit höchstens sechs Nachkommastellen.");
}

/**
 * A weight in per cent — `NUMERIC(6,3)`, `app/models/configuration.py:167` (LV §54).
 *
 * Required: a criticality weight is `NOT NULL`. That the weights add up to 100 is the
 * server's rule over the whole profile, not this field's.
 */
export function pflichtprozent() {
  return pflichtdezimal(3, "Prozentwert mit höchstens drei Nachkommastellen.");
}

/**
 * A score — `ScoreColumn`, `NUMERIC(6,2)`, `app/db/base.py:102` (LV §55).
 *
 * Two places like money, and deliberately not `betrag()`: a class boundary is not an
 * amount, and the refusal should not say "Betrag".
 */
export function pflichtpunktzahl() {
  return pflichtdezimal(2, "Punktzahl mit höchstens zwei Nachkommastellen.");
}

/**
 * Hours a time entry books — `NUMERIC(10,2)`, `app/models/work_orders.py` `TimeEntry.hours`
 * (LV §117).
 *
 * A duration, not money and not a quantity: `menge()` would accept `3,5551`, which the
 * column silently rounds, and `betrag()` fits the scale but its refusal says "Betrag".
 */
export function pflichtstunden() {
  return pflichtdezimal(2, "Stunden mit höchstens zwei Nachkommastellen.");
}

/**
 * A whole number — a count, a number of days.
 *
 * `Number` is safe here in a way it is not for `betrag` or `menge`: the value has already been
 * proved to be digits only, and the API's type for these fields is `integer`, not a
 * decimal string. The guard runs first precisely so `Number` never sees anything else.
 */
export function ganzzahl() {
  return z
    .string()
    .trim()
    .refine((wert) => wert === "" || /^\d+$/.test(wert), {
      message: "Ganze Zahl ohne Nachkommastellen.",
    })
    .transform((wert) => (wert === "" ? null : Number(wert)));
}

/** A whole number the request body cannot omit. */
export function pflichtganzzahl() {
  return z
    .string()
    .trim()
    .min(1)
    .refine((wert) => /^\d+$/.test(wert), {
      message: "Ganze Zahl ohne Nachkommastellen.",
    })
    .transform((wert) => Number(wert));
}

/**
 * A choice the user must make.
 *
 * Shape only — that the field is not empty. The permitted codes are **not** listed here:
 * they come from the server through `useLabels()`, a client-side list would be a second
 * copy free to drift, and a code this build has never heard of is the server's to refuse
 * (LV §134, §187).
 */
export function pflichtauswahl() {
  return z.string().trim().min(1);
}

/**
 * Several choices at once — a list of codes or ids, possibly empty.
 *
 * Shape only, like `pflichtauswahl()`: each entry is a non-empty string, and which ones
 * exist is the server's to know. Whether at least one must be chosen is that endpoint's
 * rule; a form that mirrors one adds `.min(1)` and says where the server states it.
 */
export function mehrfachauswahl() {
  return z.array(z.string().trim().min(1));
}

/** A choice that may be left alone. Empty becomes `null`. */
export function auswahl() {
  return z
    .string()
    .trim()
    .transform((wert) => (wert === "" ? null : wert));
}

/** A date without a time, as the API takes it (`2026-08-14`, LV §116). */
export function datum() {
  return z
    .string()
    .trim()
    .refine((wert) => wert === "" || /^\d{4}-\d{2}-\d{2}$/.test(wert), {
      message: "Datum im Format JJJJ-MM-TT.",
    })
    .transform((wert) => (wert === "" ? null : wert));
}

/**
 * A date and time on the plant's clock, as `<input type="datetime-local">` gives it —
 * `2026-10-16T08:00` (#483). Empty becomes `null`.
 *
 * The output is still the plant's wall-clock time, not the API's UTC instant (LV §116):
 * which instant it is depends on the plant's zone (LV §197), which the form learns from
 * the server at run time, so the call site converts with `plantLocalToUtcIso` rather than
 * a transform here. A date no calendar has (`2026-02-30`) is refused, so the conversion
 * never meets one.
 */
export function ortszeit() {
  return z
    .string()
    .trim()
    .refine((wert) => wert === "" || istOrtszeit(wert), {
      message: "Datum und Uhrzeit im Format JJJJ-MM-TT hh:mm.",
    })
    .transform((wert) => (wert === "" ? null : wert));
}

/** A date the request body cannot omit. */
export function pflichtdatum() {
  return z
    .string()
    .trim()
    .min(1)
    .refine((wert) => /^\d{4}-\d{2}-\d{2}$/.test(wert), {
      message: "Datum im Format JJJJ-MM-TT.",
    });
}

/**
 * The spellings of a UUID the API accepts — exactly these, measured 02.10.2026 against the
 * backend's own Pydantic: hyphenated, 32 hex digits without hyphens, braced with hyphens,
 * and `urn:uuid:` (lower-case prefix) with hyphens; hex digits in either case. A leading
 * or trailing space, misplaced hyphens and every wrong length are refused there.
 */
const HEX = "[0-9a-fA-F]";
const MIT_BINDESTRICHEN = `${HEX}{8}-${HEX}{4}-${HEX}{4}-${HEX}{4}-${HEX}{12}`;
const UUID_SCHREIBWEISEN = [
  new RegExp(`^${MIT_BINDESTRICHEN}$`),
  new RegExp(`^${HEX}{32}$`),
  new RegExp(`^\\{${MIT_BINDESTRICHEN}\\}$`),
  new RegExp(`^urn:uuid:${MIT_BINDESTRICHEN}$`),
];

/**
 * A reference to another record by its id, which may be left empty — the second of the
 * missing field helpers (LV §6).
 *
 * Empty becomes `null`, and here `null` is an instruction rather than an absence: the
 * services read it as "remove the reference" (`HazardAssessmentRequest` says so in as
 * many words), so an emptied field must reach the body, not drop out of it.
 *
 * Shape only, and no stricter than the server: trimming is the one liberty taken. Whether
 * the id names a record of the right kind, at a site this user may see, is the server's
 * to say — a document of the wrong type is refused there, with a sentence that says so.
 */
export function verweis() {
  return z
    .string()
    .trim()
    .refine(
      (wert) => wert === "" || UUID_SCHREIBWEISEN.some((form) => form.test(wert)),
      { message: "Kennung im Format xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx." },
    )
    .transform((wert) => (wert === "" ? null : wert));
}

/** A checkbox. */
export function jaNein() {
  return z.boolean();
}

/**
 * A reason, where one is optional on the wire.
 *
 * LV §104 requires a *typed* reason for some actions; those fields use `pflichttext`
 * instead. This is the other case — a free note that is sent when given and omitted when
 * not, which is `undefined` rather than `null` because the services treat an absent
 * `reason` as "none supplied" rather than "cleared".
 */
export function grund(maxLaenge = 1000) {
  return z
    .string()
    .trim()
    .max(maxLaenge)
    .transform((wert) => (wert === "" ? undefined : wert));
}

/**
 * A file the request body cannot omit — the first of the missing field helpers (LV §6).
 *
 * The input side is `File | null` and the output side is `File`, because those are two
 * different things: `null` is the state a fresh dialog is in, and the schema's job is to
 * refuse to produce a request body while it lasts. `.refine` with a type predicate is what
 * narrows the one to the other, so a `mutationFn` receives a `File` and needs no
 * `if (!file) throw` of its own — which is what both upload dialogs had, as a runtime
 * throw standing in for a validation rule.
 *
 * **Nothing about the file itself is checked here, and that is deliberate.** Size,
 * extension, MIME type and malware are steps 1-5 of the LV §75 sequence and they run on
 * the server, on the bytes it actually received. A client-side extension check would be a
 * second copy of a rule that is a *security* control in its first copy — and one that
 * reads the name the browser reports rather than the content (LV §75, §134, §223).
 */
export function datei() {
  return z
    .union([z.instanceof(File), z.null()])
    .refine((wert): wert is File => wert !== null, {
      message: "Bitte eine Datei wählen.",
    });
}
